import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SectionBadge from "@/components/site/SectionBadge";
import { imageForPath } from "@/data/images";
import { track } from "@/lib/analytics";

const GRID = "max-w-7xl mx-auto px-6 sm:px-8 md:px-12";

/**
 * Related services / capabilities links — compact cards grid.
 * links: [{ title, body, to }]
 */
export default function RelatedLinks({
  testid,
  badge = "Related",
  headline,
  links,
  tone = "alt",
}) {
  return (
    <section
      data-testid={testid}
      className={`py-16 md:py-20 ${tone === "alt" ? "bg-[#F2F2F2]" : "bg-white"}`}
    >
      <div className={GRID}>
        <SectionBadge label={badge} />
        {headline && (
          <p className="mt-6 font-serif-display text-2xl sm:text-3xl leading-snug max-w-2xl">
            {headline}
          </p>
        )}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 reveal stagger-reveal">
          {links.map((r, i) => (
            <Link
              key={r.title}
              to={r.to}
              data-testid={`${testid}-link-${i}`}
              onClick={() => track("related_link_open", { title: r.title })}
              className="group relative bg-white border border-zinc-200 p-7 flex flex-col transition-all duration-500 hover:border-black hover:-translate-y-1"
            >
              {(r.image || imageForPath(r.to)) && (
                <div className="-mx-7 -mt-7 mb-5 aspect-[16/10] overflow-hidden bg-zinc-200">
                  <img
                    src={r.image || imageForPath(r.to)}
                    alt={r.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
              )}
              <h3 className="font-serif-display text-xl leading-snug text-black">{r.title}</h3>
              {r.body && (
                <p className="mt-3 text-sm text-zinc-600 leading-relaxed flex-1">{r.body}</p>
              )}
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-black">
                Explore {r.title}
                <ArrowUpRight className="h-4 w-4 text-zinc-400 group-hover:text-black transition-colors" />
              </span>
              <span
                aria-hidden
                className="absolute left-0 bottom-0 h-[3px] w-0 bg-black transition-all duration-500 group-hover:w-full"
              />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
